/**
 * Browser Compatibility Check Utility
 * Detects required browser features and provides compatibility information.
 */

/**
 * Browser compatibility result
 */
export interface BrowserCheckResult {
  /** Whether browser is compatible */
  isCompatible: boolean;
  /** Whether WebGL is supported */
  webGLSupported: boolean;
  /** Error message if incompatible */
  errorMessage?: string;
}

/**
 * Checks if WebGL is supported.
 * @returns True if WebGL is supported
 */
function checkWebGLSupport(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return gl !== null && gl instanceof WebGLRenderingContext;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if required browser features are available.
 * @returns Browser compatibility result
 */
export function checkBrowserCompatibility(): BrowserCheckResult {
  const webGLSupported = checkWebGLSupport();

  if (!webGLSupported) {
    return {
      isCompatible: false,
      webGLSupported: false,
      errorMessage:
        'Your browser does not support WebGL, which is required for this application. Please use a modern browser.',
    };
  }

  // Check for fetch API
  if (typeof fetch === 'undefined') {
    return {
      isCompatible: false,
      webGLSupported: true,
      errorMessage:
        'Your browser does not support the Fetch API. Please use a modern browser.',
    };
  }

  // Check for Promise support
  if (typeof Promise === 'undefined') {
    return {
      isCompatible: false,
      webGLSupported: true,
      errorMessage:
        'Your browser does not support Promises. Please use a modern browser.',
    };
  }

  return {
    isCompatible: true,
    webGLSupported: true,
  };
}

/**
 * Gets a user-friendly error message for incompatible browsers.
 * @param result - Browser check result
 * @returns Error message or null if compatible
 */
export function getBrowserErrorMessage(
  result: BrowserCheckResult
): string | null {
  if (result.isCompatible) {
    return null;
  }
  return result.errorMessage || 'Your browser is not supported. Please use a modern browser.';
}

